import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { useLanguage } from '../context/LanguageContext';
const BOOK_COVER_URL = 'https://uploadthingy.s3.us-west-1.amazonaws.com/jAJcVEMtHQ4Lav5MFqar7J/1be62b6e-3cdc-4efb-9827-4d03fb6466ec.png';
export function Hero() {
  const {
    t,
    language
  } = useLanguage();
  const hero = t.hero;
  return <section className="min-h-screen flex items-center justify-center relative overflow-hidden matrix-grid" data-id="element-39">
      {/* Animated radial glows */}
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-matrix-accent/10 rounded-full blur-[120px] pointer-events-none" animate={{
      scale: [1, 1.2, 1],
      opacity: [0.1, 0.15, 0.1]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-40" />

      <div className="container mx-auto px-4 py-20 relative z-20" data-id="element-41">
        <div className="max-w-4xl mx-auto" data-id="element-42">
          {/* Hero Text */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="text-center" data-id="element-43">
            {/* Hook - Enhanced for maximum impact */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.5,
            duration: 0.6,
            ease: 'easeOut'
          }} className="relative mb-8" data-id="element-44">
              {/* Glowing background effect */}
              <motion.div className="absolute inset-0 bg-gradient-to-r from-matrix-accent/20 via-matrix-accent-secondary/20 to-matrix-accent/20 blur-3xl" animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-45" />

              {/* Main hook text with enhanced styling */}
              <motion.p className="relative text-2xl md:text-3xl lg:text-4xl font-black mb-6 leading-tight px-4" animate={{
              textShadow: ['0 0 20px rgba(0, 217, 255, 0.8), 0 0 40px rgba(0, 217, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.8)', '0 0 30px rgba(0, 217, 255, 1), 0 0 60px rgba(0, 217, 255, 0.6), 0 2px 4px rgba(0, 0, 0, 0.8)', '0 0 20px rgba(0, 217, 255, 0.8), 0 0 40px rgba(0, 217, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.8)']
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} style={{
              background: 'linear-gradient(135deg, #00d9ff 0%, #00ffcc 50%, #00d9ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              backgroundSize: '200% 200%'
            }} data-id="element-46">
                <motion.span animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }} transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'linear'
              }} style={{
                background: 'linear-gradient(135deg, #00d2f7 0%, #00ffcc 50%, #00d2f7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                backgroundSize: '200% 200%',
                display: 'inline-block'
              }} data-id="element-47">
                  {hero.hook}
                </motion.span>
              </motion.p>

              {/* Decorative underline */}
              <motion.div initial={{
              width: 0
            }} animate={{
              width: '80%'
            }} transition={{
              delay: 0.8,
              duration: 0.8,
              ease: 'easeOut'
            }} className="h-1 mx-auto bg-gradient-to-r from-transparent via-matrix-accent to-transparent relative" data-id="element-48">
                <motion.div className="absolute inset-0 bg-gradient-to-r from-matrix-accent to-matrix-accent-secondary" animate={{
                opacity: [0.5, 1, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-49" />
              </motion.div>
            </motion.div>

            {/* Main content with enhanced text shadows */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="space-y-4 text-white text-base md:text-lg leading-relaxed mb-8 max-w-3xl mx-auto" data-id="element-50">
              <p className="opacity-95" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-51">
                {hero.intro1}
              </p>
              <p className="italic font-medium" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-52">
                {hero.intro2}
              </p>
              <p className="opacity-95" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-53">
                {hero.intro3}
              </p>
              <p className="opacity-95" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-54">
                {hero.intro4}
              </p>
              <p className="font-medium" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-55">
                {hero.intro5}
              </p>
              <p className="opacity-95" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
            }} data-id="element-56">
                {hero.intro6}
              </p>
              <p className="text-matrix-accent font-semibold text-lg" style={{
              textShadow: '0 0 20px rgba(0, 217, 255, 0.8), 0 0 40px rgba(0, 217, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.8)'
            }} data-id="element-57">
                {hero.intro7}
              </p>
            </motion.div>

            {/* Book Cover */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.7,
            ease: 'easeOut'
          }} className="relative mb-12 flex justify-center" data-id="element-58">
              {/* Glow effect behind book */}
              <div className="absolute inset-0 bg-matrix-accent/20 blur-3xl rounded-full scale-110" data-id="element-59" />

              <motion.img src={BOOK_COVER_URL} alt={language === 'es' ? 'ROMPE LA MATRIX 3-6-9 - Manual práctico para despertar' : 'BREAK THE MATRIX 3-6-9 - Practical manual to awaken'} className="w-64 md:w-80 lg:w-96 rounded-lg shadow-2xl relative z-10" style={{
              boxShadow: '0 0 60px rgba(0, 217, 255, 0.3), 0 25px 50px rgba(0, 0, 0, 0.5)'
            }} animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-60" />
            </motion.div>

            {/* Title - Enhanced with card container */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.9
          }} className="mb-8" data-id="element-61">
              {/* Card container for title section */}
              <div className="bg-black/60 backdrop-blur-sm border border-matrix-accent/30 rounded-2xl p-8 md:p-10 max-w-3xl mx-auto" data-id="element-62">
                <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white mb-4 leading-tight text-center" style={{
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.9), 0 0 30px rgba(0, 0, 0, 0.6)'
              }} data-id="element-63">
                  {hero.title}{' '}
                  <span className="text-matrix-accent text-glow" style={{
                  textShadow: '0 0 20px rgba(0, 217, 255, 0.8), 0 0 40px rgba(0, 217, 255, 0.4), 0 2px 4px rgba(0, 0, 0, 0.8)'
                }} data-id="element-64">
                    3-6-9
                  </span>
                </h1>
                <p className="text-white/90 text-base md:text-lg mb-6 text-center" style={{
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.5)'
              }} data-id="element-65">
                  {hero.subtitle}
                </p>

                {/* CTA inside card */}
                <div className="flex justify-center" data-id="element-66">
                  <CTAButton size="large" href="#pricing" data-id="element-67">
                    {hero.cta}
                  </CTAButton>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20" data-id="element-68">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center" data-id="element-69">
          <motion.div animate={{
          y: [0, 12, 0],
          opacity: [1, 0, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="w-1.5 h-3 bg-matrix-accent rounded-full mt-2" data-id="element-70" />
        </motion.div>
      </motion.div>
    </section>;
}